import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useClickAway } from 'react-use'
import {
  CheckboxGroup,
  SearchableSelect,
  ScrollErrorForm,
  DateInput,
} from 'components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { SubmitButton } from 'lp-components'
import {
  AccountAudience,
  isGfBDAgreementsEnabled,
  UserRole,
} from 'config/portal'
import { ProductAgreementType } from '../../../config/products'
import { ServicesAgreementStatus } from 'config/automatic-rollovers'
import { ServicesAgreementStatus as TaxableAccountsServicesAgreementStatus } from 'config/taxable-accounts'
import { uniq, compact } from 'lodash'
import * as Types from 'types'
import { sortSelectOptions } from 'utils'
import { selectors as globalSelectors } from 'global-reducer'
import { compose } from 'redux'
import { connect } from 'react-redux'

// Convert enums from config to list as options for form inputs
const agreementStatusOptions = Object.values(ServicesAgreementStatus)
const taxableAccountsAgreementStatusOptions = Object.values(
  TaxableAccountsServicesAgreementStatus
)
const agreementStatusOptionsForPlanSponsor = agreementStatusOptions.filter(
  (agreementStatus) =>
    agreementStatus !== ServicesAgreementStatus.PENDING_SIGNATURES
)
const taxableAccountsAgreementStatusOptionsForPlanSponsor =
  taxableAccountsAgreementStatusOptions.filter(
    (agreementStatus) =>
      agreementStatus !==
      TaxableAccountsServicesAgreementStatus.PENDING_SIGNATURES
  )

const propTypes = {
  filters: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  clearAllFilters: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  servicesAgreements: PropTypes.arrayOf(Types.servicesAgreementSummary)
    .isRequired,
  bdPlanAgreements: PropTypes.arrayOf(Types.bdPlanAgreementsSummary).isRequired,
  currentUser: Types.user.isRequired,
  agreementsType: PropTypes.string.isRequired,
}

const defaultProps = {}

function FakeLegend({ label }) {
  return <label>{label}</label>
}

function PlanAgreementsFilterDialog({
  filters,
  handleSubmit,
  clearAllFilters,
  handleClose,
  servicesAgreements,
  bdPlanAgreements,
  currentUser,
  agreementsType,
}) {
  const ref = useRef(null)

  useClickAway(ref, (e) => {
    const targetEl = e.target
    if (!document.body.contains(targetEl)) return
    handleClose()
  })

  // dynamically generate plan sponsor and agreement type select options
  const uniquePlanSponsorList = uniq(
    compact(servicesAgreements.map((a) => a.planSponsorCompany.name))
  )
  const uniqueBDPlanSponsorList =
    agreementsType === ProductAgreementType.BD
      ? uniq(compact(bdPlanAgreements.map((a) => a.planSponsorName)))
      : []

  const uniqueInitialInvestmentList = uniq(
    compact(servicesAgreements.map((a) => a.portalAgreementType))
  )

  const filterSchema = Yup.object().shape({
    agreementStatus: Yup.array().test(
      'Valid agreement status',
      `Invalid value(s). Must be one of the following: ${agreementStatusOptions.join(
        ', '
      )}`,
      (val) => {
        return val.every((el) => agreementStatusOptions.includes(el))
      }
    ),
    initialInvestment: Yup.array().test(
      'Valid initial investment',
      `Invalid value(s). Must select valid initial investments.`,
      (val) => {
        return val.every((el) => uniqueInitialInvestmentList.includes(el))
      }
    ),
    planSponsor: Yup.array().test(
      'Valid plan sponsor',
      `Invalid value(s). Must select valid plan sponsors.`,
      (val) => {
        if (agreementsType === ProductAgreementType.BD)
          return val.every((el) => uniqueBDPlanSponsorList.includes(el))
        else return val.every((el) => uniquePlanSponsorList.includes(el))
      }
    ),
    startLastUpdatedDate: Yup.date(),
    endLastUpdatedDate: Yup.date(),
  })

  const isBenefitDistributionsTable = agreementsType === ProductAgreementType.BD
  const agreementStatusLabel =
    isBenefitDistributionsTable && !isGfBDAgreementsEnabled()
      ? 'Addendum status'
      : 'Agreement status'

  return (
    <div
      className="filter-formik-container"
      ref={ref}
      onBlur={(e) => {
        if (ref.current.contains(e.relatedTarget ?? e.target)) return
        handleClose()
      }}
    >
      <Formik
        initialValues={filters}
        validationSchema={filterSchema}
        onSubmit={(values) => {
          handleSubmit(values)
          handleClose()
        }}
      >
        {({
          values: { startLastUpdatedDate, endLastUpdatedDate },
          isSubmitting,
        }) => (
          <ScrollErrorForm>
            <div className="services-agreements-filter-aro filter-dialog-container-aro">
              <div className="filter-row">
                {currentUser.accountAudience ===
                  AccountAudience.PLAN_FIDUCIARY &&
                currentUser.retirementServicesPortalProfile !==
                  UserRole.ADMIN ? (
                  <CheckboxGroup
                    name="agreementStatus"
                    label={agreementStatusLabel}
                    options={sortSelectOptions(
                      agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS
                        ? taxableAccountsAgreementStatusOptionsForPlanSponsor
                        : agreementStatusOptionsForPlanSponsor
                    )}
                    labelComponent={FakeLegend}
                  />
                ) : (
                  <CheckboxGroup
                    name="agreementStatus"
                    label={agreementStatusLabel}
                    options={
                      agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS
                        ? taxableAccountsAgreementStatusOptions
                        : agreementStatusOptions
                    }
                    labelComponent={FakeLegend} // legends don't respond to flex styling
                  />
                )}
              </div>
              {isBenefitDistributionsTable ? null : (
                <div className="filter-row">
                  <SearchableSelect
                    name="initialInvestment"
                    label="Initial investment"
                    options={sortSelectOptions(uniqueInitialInvestmentList)}
                    isMulti={true}
                    placeholder="Select initial investment(s)"
                    marginTop="0"
                  />
                </div>
              )}
              {currentUser.accountAudience !==
                AccountAudience.PLAN_FIDUCIARY && (
                <div className="filter-row">
                  <SearchableSelect
                    name="planSponsor"
                    label="Plan sponsor"
                    options={
                      agreementsType === ProductAgreementType.BD
                        ? sortSelectOptions(uniqueBDPlanSponsorList)
                        : sortSelectOptions(uniquePlanSponsorList)
                    }
                    isMulti={true}
                    placeholder="Select plan sponsor(s)"
                    marginTop="0"
                  />
                </div>
              )}
              <div className="filter-row">
                <fieldset aria-labelledby="date-range-legend">
                  <div className="fake-legend" id="date-range-legend">
                    Date created
                  </div>
                  <div id="filter-date-range">
                    <DateInput
                      name="startLastUpdatedDate"
                      label={false}
                      aria-label="start date range of last updated date"
                      selectsStart
                      startDate={startLastUpdatedDate}
                      endDate={endLastUpdatedDate}
                    />
                    <div id="date-separator" aria-hidden="true">
                      -
                    </div>
                    <DateInput
                      name="endLastUpdatedDate"
                      label={false}
                      aria-label="end date range of last updated date"
                      selectsEnd
                      startDate={startLastUpdatedDate}
                      endDate={endLastUpdatedDate}
                      minDate={startLastUpdatedDate}
                    />
                  </div>
                </fieldset>
              </div>
              <div className="filter-row filter-actions-container">
                <div className="filter-actions">
                  <button
                    type="button"
                    id="filter-btn-clear"
                    className="button-text"
                    onClick={() => {
                      clearAllFilters()
                      handleClose()
                    }}
                  >
                    Clear all filters
                  </button>
                </div>
                <div className="filter-actions">
                  <button
                    type="button"
                    id="filter-btn-cancel"
                    className="button-text"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <SubmitButton submitting={isSubmitting}>
                    Apply filters
                  </SubmitButton>
                </div>
              </div>
            </div>
          </ScrollErrorForm>
        )}
      </Formik>
    </div>
  )
}

PlanAgreementsFilterDialog.propTypes = propTypes
PlanAgreementsFilterDialog.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentUser: globalSelectors.currentUser(state),
  }
}

export default compose(connect(mapStateToProps, null))(
  PlanAgreementsFilterDialog
)
